/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import { Tab, Tabs } from '@mui/material';
import { isEqual } from 'lodash';
import {
    FC, useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as FiltersIcon } from '../../../assets/images/filters.svg';
import {
    BillingAction, BillingAsset, BillingEvent, BillingEventFilterParams,
} from '../../../types/billing';
import { ButtonVariant, OrderOptions } from '../../../types/general';
import { formatToLocalDate } from '../../../utils/date';
import { BillingContext, withBillingContext } from '../../controllers/BillingContext';
import { TranslationContext, withTranslationContext } from '../../controllers/TranslationContext';
import { Button } from '../../elements/Button';
import { DatePicker } from '../../elements/DatePicker';
import { Drawer } from '../../elements/Drawer';
import InfiniteScrollWrapper from '../../elements/InfiniteScrollWrapper';
import { LoadingCircles } from '../../elements/LoadingCircles';
import { ErrorResponse } from '../../../types/errors';
import { AppRoute } from '../../../constants/routes';

export const BillingMovementDefaultFilters: BillingEventFilterParams = {
    _cursor: '',
    _limit: '',
    _sort: OrderOptions.DESCENDING,
    billingActions: [BillingAction.PURCHASE, BillingAction.REFUND],
    billingAssets: [BillingAsset.CREDITS],
};

type OwnProps = TranslationContext & BillingContext;

const BillingCreditMovementsScreenBase: FC<OwnProps> = (props) => {
    const {
        t,
        getBillingEvents,
        organizationWallet,
    } = props;

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [filters, setFilters] = useState<BillingEventFilterParams>(BillingMovementDefaultFilters);
    const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [listLoadingError, setListLoadingError] = useState<ErrorResponse>();

    const prevFilters = useRef<BillingEventFilterParams>();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const { _cursor, ...rest } = filters;
        const { _cursor: prevCursor, ...prevRest } = prevFilters.current || {};
        
        if (!isEqual(rest, prevRest)) {
            setBillingEvents([]);
            prepareBillingMovement(true);
        }

        prevFilters.current = filters;
    }, [organizationWallet, filters]);

    const prepareBillingMovement = async (reset?: boolean) => {
        if (!organizationWallet) return;
        
        setIsLoading(true);
        setListLoadingError(undefined);

        const [data, error] = await getBillingEvents(String(organizationWallet?.id) || '', { ...filters, _cursor: reset ? '' : filters._cursor });
        setIsLoading(false);

        if (error) {
            setListLoadingError(error);
            return;
        }

        setBillingEvents((prev) => (!reset ? [...prev, ...data.results] : [...data.results]));
        setFilters((prev) => ({
            ...prev,
            _cursor: data.cursor,
        }));
    };

    const renderDescription = (event: BillingEvent) => {
        const billingAction = t(`billingMovementScreen.actions.${event.billingAction}`);
        const billingAsset = t(`billingMovementScreen.assets.${event.billingAsset}`);

        return `${billingAction} ${billingAsset}`;
    };

    const handleDateChange = (date: Date | null, field: string) => {
        if (!date || isNaN(date?.getTime())) {
            return;
        }
        setFilters((prev) => ({
            ...prev,
            [field]: date ? date.toISOString() : '',
        }));
    };

    const handleEndDateChange = (date: Date | null, field: string) => {
        if (!date || isNaN(date?.getTime())) {
            return;
        }
        date?.setHours(23, 59, 59, 999);
        handleDateChange(date, field);
    };

    const clearFilters = () => {
        setIsOpenFilters(false);
        setFilters(BillingMovementDefaultFilters);
    };

    const onFilterSubmit = () => {
        setIsOpenFilters(false);
    };

    return (
        <section className="billing-credit-movements-screen">
            <div className="billing-credit-movements-screen__header">
                <div className="billing-credit-movements-screen__header__title">
                    <h1>{t('billingMovementScreen.title')}</h1>
                    <Button
                        extraClasses="circle-btn filters-trigger-btn"
                        onClick={() => setIsOpenFilters(true)}
                        testId="mobile-filter-btn"
                    >
                        <FiltersIcon />
                    </Button>
                </div>
                <Tabs value={location.pathname} onChange={(_, val) => navigate(val)} data-testid="tab-list" role="navigation">
                    <Tab label={t('billingMovementScreen.tabs.credits')} value={AppRoute.OrganizationBillingCreditMovements} />
                    <Tab label={t('billingMovementScreen.tabs.debits')} value={AppRoute.OrganizationBillingDebitMovements} />
                    <Tab label={t('billingMovementScreen.tabs.subscription')} value={AppRoute.OrganizationBillingMovementsSubscription} />
                </Tabs>
            </div>
            <div className="billing-credit-movements-screen__content">
                <div className="billing-credit-movements-screen__content__filters">
                    <div className="billing-credit-movements-screen__content__filters__filter">
                        <DatePicker
                            name="minDate"
                            value={filters.minDate}
                            onChange={handleDateChange}
                            label={t('billingMovementScreen.filters.minDateLabel')}
                        />
                        <DatePicker
                            name="maxDate"
                            value={filters.maxDate}
                            onChange={handleDateChange}
                            label={t('billingMovementScreen.filters.maxDateLabel')}
                        />
                        <Button
                            onClick={clearFilters}
                            testId="clear-filters"
                        >
                            {t('billingMovementScreen.filters.cleanAll')}
                        </Button>
                    </div>
                </div>
                <div className="billing-credit-movements-screen__content__header">
                    <h3>{t('billingMovementScreen.labels.date')}</h3>
                    <h3>{t('billingMovementScreen.labels.user')}</h3>
                    <h3>{t('billingMovementScreen.labels.description')}</h3>
                    <h3>{t('billingMovementScreen.labels.value')}</h3>
                </div>
                {isLoading && (<LoadingCircles size="m" variant="primary" />)}
                {!isLoading && billingEvents.length === 0 && (
                    <p>{t('billingMovementScreen.noResultsFound')}</p>
                )}
                {!isLoading && listLoadingError && (
                    <p>{listLoadingError.errors.map((e) => e.getMessageTranslated(t))}</p>
                )}
                <InfiniteScrollWrapper
                    hasMore={!!filters._cursor}
                    requestMore={prepareBillingMovement}
                    extraClasses="billing-credit-movements-screen__content__list"
                >
                    {billingEvents.map((billingEvent) => (
                        <article className="billing-credit-movements-screen__content__list__item" key={billingEvent.id} data-testid={`billing-event-${billingEvent.id}`}>
                            <div className="billing-credit-movements-screen__content__list__item__date">
                                <h4>{t('billingMovementScreen.labels.date')}</h4>
                                <p>{formatToLocalDate(billingEvent.createdDate)}</p>
                            </div>
                            <div className="billing-credit-movements-screen__content__list__item__name">
                                <h4>{t('billingMovementScreen.labels.user')}</h4>
                                <p>{billingEvent.userFullName}</p>
                            </div>
                            <div className="billing-credit-movements-screen__content__list__item__description">
                                <h4>{t('billingMovementScreen.labels.description')}</h4>
                                <p>{renderDescription(billingEvent)}</p>
                            </div>
                            <div className="billing-credit-movements-screen__content__list__item__value" data-amount={billingEvent.amount} data-operation="credit">
                                <p>{t(`billingMovementScreen.valueByBillingAsset.${billingEvent.billingAsset}`, {
                                    billingEvent,
                                })}
                                </p>
                            </div>
                        </article>
                    ))}
                </InfiniteScrollWrapper>
            </div>
            <Drawer
                open={isOpenFilters}
                handleClose={() => setIsOpenFilters(false)}
                title={t('general.filter')}
                testId="filters-drawer"
                extraClasses="filters-drawer"
            >
                <form className="filters-form" data-testid="filters-form">
                    <div className="filters-form__filters">
                        <h3>{t('billingMovementScreen.filters.dateRange')}</h3>
                        <DatePicker
                            name="minDate"
                            value={filters.minDate}
                            onChange={handleDateChange}
                            label={t('billingMovementScreen.filters.minDateLabel')}
                        />
                        <DatePicker
                            name="maxDate"
                            value={filters.maxDate}
                            onChange={handleEndDateChange}
                            label={t('billingMovementScreen.filters.maxDateLabel')}
                        />
                    </div>
                    <div className="filters-form__buttons">
                        <Button
                            variant={ButtonVariant.Curved}
                            extraClasses="secondary clean-btn"
                            onClick={clearFilters}
                            testId="clear-filters"
                        >
                            {t('billingMovementScreen.filters.cleanAll')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Curved}
                            onClick={onFilterSubmit}
                            testId="submit-btn"
                        >
                            {t('general.applyFilters')}
                        </Button>
                    </div>
                </form>
            </Drawer>
        </section>
    );
};

export const BillingCreditMovementsScreen = withBillingContext(withTranslationContext(BillingCreditMovementsScreenBase));
